import { PAYMENT_STATUS } from '../enums/payment';
import { SHIPMENT_STATUS } from '../enums/shipment';

/**
 * Enum for order table fields
 * @readonly
 * @enum {Array}
 */
export const ORDER_TABLE_FIELDS = Object.freeze([
	{
		key: 'checkbox',
		label: '',
		class: 'checkbox-col',
		cellClass: 'checkbox-cel',
	},
	{
		key: 'order_code',
		label: 'Order ID',
		class: 'order-col',
		cellClass: 'order-cel',
	},
	{
		key: 'thunder_order_id',
		label: 'Thunder Order ID:',
		class: 'thunder-order-id-col',
		cellClass: 'thunder-order-id-cel',
	},
	{
		key: 'synced_with_thunder_at',
		label: 'Synced With Thunder At',
		class: 'synced-with-thunder-col',
		cellClass: 'synced-with-thunder-cel',
	},
	{
		key: 'order_channel_id',
		label: 'Channel order ID:',
		class: 'order-channel-id-col',
		cellClass: 'order-channel-id-cel',
	},
	{
		key: 'status',
		label: 'Status',
		class: 'status-col',
		cellClass: 'status-cel',
	},
	{
		key: 'reconciled_at',
		label: 'Reconciled at',
		class: 'reconciled-col',
		cellClass: 'reconciled-cel',
	},
	{
		key: 'created',
		label: 'Created date',
		class: 'created-col',
		cellClass: 'created-cel',
	},
	{
		key: 'channel_name',
		label: 'Channel',
		class: 'channel-col',
		cellClass: 'channel-cel',
	},
	{
		key: 'customer_name',
		label: 'Customer',
		class: 'customer-col',
		cellClass: 'customer-cel',
	},
	{
		key: 'phone_number',
		label: 'Phone No.',
		class: 'phone-number-col',
		cellClass: 'phone-number-cel',
	},
	{
		key: 'email',
		label: 'Email',
		class: 'email-col',
		cellClass: 'email-cel',
	},
	{
		key: 'payment_method',
		label: 'Payment method',
		class: 'payment-method-col',
		cellClass: 'payment-method-cel',
	},
	{
		key: 'total',
		label: 'Total price',
		class: 'total-col',
		cellClass: 'total-cel',
	},
	{
		key: 'shipment',
		label: 'Shipment',
		class: 'shipment-col',
		cellClass: 'shipment-cel',
	},
]);

export const ORDER_DETAIL_TABLE_FIELDS = Object.freeze([
	{
		key: 'product',
		label: 'Product',
		class: 'product-col',
		cellClass: 'product-cel',
	},
	{
		key: 'priceSelling',
		label: 'Selling price',
		class: 'selling-price-col',
		cellClass: 'selling-price-cel',
	},
	{
		key: 'quantity',
		label: 'Quantity',
		class: 'quantity-col',
		cellClass: 'quantity-cel',
	},
	{
		key: 'discount',
		label: 'Discount',
		class: 'discount-col',
		cellClass: 'discount-cel',
	},
	{
		key: 'total',
		label: 'Total',
		class: 'total-col',
		cellClass: 'total-cel',
	},
]);

// order status, following from be
export const ORDER_STATUS = Object.freeze({
	CREATED: 'created',
	PREPARING: 'preparing',
	READY_TO_PICK_UP: 'ready_to_pick_up',
	PICKED_UP: 'picked_up',
	SHIPPED: 'shipped',
	PARTIALLY_SHIPPED: 'partially_shipped',
	COMPLETED: 'completed',
	EXPIRED: 'expired',
	CANCELED: 'canceled',
});

// order status, following from be
export const ORDER_STATUS_OPTIONS = Object.freeze([
	{
		name: 'All statuses',
		value: null,
	},
	{
		name: 'Preparing',
		value: ORDER_STATUS.PREPARING,
	},
	{
		name: 'Ready to pick up',
		value: ORDER_STATUS.READY_TO_PICK_UP,
	},
	{
		name: 'Pick up',
		value: ORDER_STATUS.PICKED_UP,
	},
	{
		name: 'Shipped',
		value: ORDER_STATUS.SHIPPED,
	},
	{
		name: 'Partially shipped',
		value: ORDER_STATUS.PARTIALLY_SHIPPED,
	},
	{
		name: 'Completed',
		value: ORDER_STATUS.COMPLETED,
	},
	{
		name: 'Expired',
		value: ORDER_STATUS.EXPIRED,
	},
	{
		name: 'Canceled',
		value: ORDER_STATUS.CANCELED,
	},
]);

// mapping order color, use with CBadge property components
export const ORDER_COLOR = {
	ORDER: {
		[ORDER_STATUS.CREATED]: 'info',
		[ORDER_STATUS.PREPARING]: 'hypertext',
		[ORDER_STATUS.READY_TO_PICK_UP]: 'lavender',
		[ORDER_STATUS.SHIPPED]: 'lavender',
		[ORDER_STATUS.PARTIALLY_SHIPPED]: 'lavender',
		[ORDER_STATUS.COMPLETED]: 'success',
		[ORDER_STATUS.EXPIRED]: 'alert',
		[ORDER_STATUS.CANCELED]: 'alert',
	},
	PAYMENT: {
		[PAYMENT_STATUS.UNPAID]: 'alto',
		[PAYMENT_STATUS.PENDING]: 'hypertext',
		[PAYMENT_STATUS.FAILED]: 'alert',
		[PAYMENT_STATUS.EXPIRED]: 'alert',
		[PAYMENT_STATUS.PAID]: 'success',
		[PAYMENT_STATUS.COD]: 'success',
		[PAYMENT_STATUS.PAT_AT_STORE]: 'success',
	},
	SHIPMENT: {
		[SHIPMENT_STATUS.WAITING_FOR_PAYMENT]: 'alto',
		[SHIPMENT_STATUS.WAREHOUSE_SELECTING]: 'hypertext',
		[SHIPMENT_STATUS.READY_TO_CREATE_SHIPMENT]: 'hypertext',
		[SHIPMENT_STATUS.READY_TO_BOOK_COURIER]: 'hypertext',
		[SHIPMENT_STATUS.READY_TO_SHIP]: 'hypertext',
		[SHIPMENT_STATUS.SHIPPED]: 'hypertext',
		[SHIPMENT_STATUS.DELIVERED]: 'success',
	},
};

// mapping order title, use with CBadge component
export const ORDER_TITLE = {
	ORDER: {
		[ORDER_STATUS.CREATED]: 'Created',
		[ORDER_STATUS.PREPARING]: 'Preparing',
		[ORDER_STATUS.READY_TO_PICK_UP]: 'Ready to pick up',
		[ORDER_STATUS.SHIPPED]: 'Shipped',
		[ORDER_STATUS.PARTIALLY_SHIPPED]: 'Partial shipped',
		[ORDER_STATUS.COMPLETED]: 'Completed',
		[ORDER_STATUS.EXPIRED]: 'Expired',
		[ORDER_STATUS.CANCELED]: 'Canceled',
	},
	PAYMENT: {
		[PAYMENT_STATUS.UNPAID]: 'Unpaid',
		[PAYMENT_STATUS.PENDING]: 'Pending',
		[PAYMENT_STATUS.FAILED]: 'Failed',
		[PAYMENT_STATUS.EXPIRED]: 'Expired',
		[PAYMENT_STATUS.PAID]: 'Paid',
		[PAYMENT_STATUS.COD]: 'COD',
		[PAYMENT_STATUS.PAT_AT_STORE]: 'Pay at store',
	},
	SHIPMENT: {
		[SHIPMENT_STATUS.WAITING_FOR_PAYMENT]: 'Waiting for payment',
		[SHIPMENT_STATUS.WAREHOUSE_SELECTING]: 'Warehouse selecting',
		[SHIPMENT_STATUS.READY_TO_CREATE_SHIPMENT]: 'Ready to create shipment',
		[SHIPMENT_STATUS.READY_TO_BOOK_COURIER]: 'Ready to book courier',
		[SHIPMENT_STATUS.READY_TO_SHIP]: 'Ready to ship',
		[SHIPMENT_STATUS.SHIPPED]: 'Shipped',
		[SHIPMENT_STATUS.DELIVERED]: 'Deliverd',
	},
};

/**
 * Enum for order document type
 * @readonly
 * @enum {Object}
 */
export const ORDER_DOCUMENT_TYPE = Object.freeze({
	SHIPPING_LABEL: 'shippingLabel',
	CARRIER_MANIFEST: 'carrierManifest',
	INVOICE: 'invoice',
});

export const ORDER_ITEM_TYPE = {
	FREEBIE: 'freebie',
	NORMAL: 'normal',
	PRE_ORDER: 'pre_order',
};

export const ORDER_ITEM_TYPE_TITLE = {
	[ORDER_ITEM_TYPE.FREEBIE]: 'Freebie',
	[ORDER_ITEM_TYPE.NORMAL]: 'Normal',
	[ORDER_ITEM_TYPE.PRE_ORDER]: 'Pre-order',
};

// reconciled order status, following from be
export const RECONCILED_ORDER_OPTIONS = [
	{
		name: 'All statuses',
		value: null,
	},
	{
		name: 'Reconciled',
		value: '1',
	},
	{
		name: 'Unreconciled',
		value: '0',
	},
];

export const SYNCED_WITH_THUNDER_OPTIONS = [
	{
		name: 'All statuses',
		value: null,
	},
	{
		name: 'Success',
		value: '1',
	},
	{
		name: 'Pending',
		value: '0',
	},
];
